import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { TranslocoService } from '@jsverse/transloco';
import { TelemonitoringRequestProps } from '@modules/telemonitoring/telemonitoring-request/telemonitoring-request-props';
import { TelemonitoringRequestComponent } from '@modules/telemonitoring/telemonitoring-request/telemonitoring-request.component';
import { Store } from '@ngrx/store';
import { Patient } from '@pixacare/domain';
import { DialogService } from '@services/dialog.service';
import { TeleexpertiseProvider } from '@shared/models/enums/teleexpertise-provider.enum';
import { ActionsService } from '@shared/models/helpers/actions-service';
import { MenuAction } from '@shared/models/menu-actions/menu-action';
import { MenuActionId } from '@shared/models/menu-actions/menu-action-id.enum';
import { patientsActions } from '@shared/store/patients/patients.actions';
import { selectClientCode, selectDepartmentId } from '@shared/store/router/router.selectors';
import { combineLatest, first, switchMap } from 'rxjs';
import { PatientEditComponent } from './patient-edit/patient-edit/patient-edit.component';
import { PatientLinkDirective } from './patient-link.directive';
import { PatientMergeComponent } from './patient-merge/patient-merge.component';
import { PatientMergeService } from './patient-merge/patient-merge.service';
import { PatientNamePipe } from './patient-name.pipe';
import { PatientReportComponent } from './patient-report/patient-report.component';
import { PatientTeleexpertiseRequestComponent } from './patient-teleexpertise-request/patient-teleexpertise-request.component';

@Injectable({
  providedIn: 'root',
})
export class PatientActionsService extends ActionsService {
  private readonly router = inject(Router);
  private readonly store = inject(Store);
  private readonly dialogService = inject(DialogService);
  private readonly patientMergeService = inject(PatientMergeService);
  private readonly patientNamePipe = inject(PatientNamePipe);
  private readonly translocoService = inject(TranslocoService);

  clientCode$ = this.store.select(selectClientCode);
  departmentId$ = this.store.select(selectDepartmentId);
  params$ = combineLatest({
    clientCode: this.clientCode$,
    departmentId: this.departmentId$,
  });

  actions: MenuAction<Patient>[] = [
    {
      id: MenuActionId.PATIENT_EDIT,
      label: this.translocoService.translate('Modifier'),
      testId: 'action-patient-edit',
      icon: '@tui.pencil',
      condition: (patient: Patient): boolean => this.isPatientActive(patient),
      execute: (patient: Patient): void => {
        this.clientCode$.pipe(first()).subscribe((clientCode) => {
          this.dialogService
            .openComponentWithCloseConfirmation(PatientEditComponent, {
              label: this.translocoService.translate('Modifier le patient'),
              size: 'l',
              data: {
                patient,
                clientCode,
              },
            })
            .subscribe();
        });
      },
    },
    {
      id: MenuActionId.PATIENT_ARCHIVE,
      label: this.translocoService.translate('Archiver'),
      testId: 'action-patient-archive',
      icon: '@tui.archive',
      condition: (patient: Patient): boolean => this.isPatientActive(patient),
      execute: (patient: Patient): void => {
        this.clientCode$.pipe(first()).subscribe((clientCode) => {
          this.store.dispatch(
            patientsActions.archivePatient({
              clientCode,
              patientId: patient.id,
            }),
          );
        });
      },
    },
    {
      id: MenuActionId.PATIENT_UNARCHIVE,
      label: this.translocoService.translate('Désarchiver'),
      testId: 'action-patient-unarchive',
      icon: '@tui.archive-restore',
      condition: (patient: Patient): boolean => this.isPatientArchived(patient),
      execute: (patient: Patient): void => {
        this.clientCode$.pipe(first()).subscribe((clientCode) => {
          this.store.dispatch(
            patientsActions.unarchivePatient({
              clientCode,
              patientId: patient.id,
            }),
          );
        });
      },
    },
    {
      id: MenuActionId.PATIENT_REPORT,
      label: this.translocoService.translate('Rapport'),
      testId: 'action-patient-report',
      icon: '@tui.file-text',
      execute: (patient: Patient): void => {
        const title = this.translocoService.translate('Rapport de suivi de {{name}}', {
          name: this.patientNamePipe.transform(patient),
        });
        combineLatest([this.store.select(selectClientCode), this.store.select(selectDepartmentId)])
          .pipe(
            first(),
            switchMap(([clientCode, departmentId]) =>
              this.dialogService.openComponent(PatientReportComponent, {
                appearance: 'page',
                data: {
                  clientCode,
                  patient,
                  title,
                  departmentIds: departmentId ? [departmentId] : [],
                },
              }),
            ),
          )
          .subscribe();
      },
    },
    {
      id: MenuActionId.PATIENT_TELEMONITOR,
      label: this.translocoService.translate('Télésurveiller'),
      testId: 'action-patient-telemonitor',
      icon: 'telemonitoring',
      condition: (patient: Patient): boolean => this.isPatientActive(patient),
      execute: (patient: Patient): void => {
        this.params$.pipe(first()).subscribe(({ clientCode, departmentId }) => {
          this.dialogService
            .openComponent<TelemonitoringRequestComponent, TelemonitoringRequestProps>(TelemonitoringRequestComponent, {
              label: this.translocoService.translate('Initier un télésuivi'),
              size: 'l',
              appearance: 'fullscreen',
              data: {
                patient,
                clientCode,
                departmentIds: departmentId ? [departmentId] : [],
              },
            })
            .subscribe();
        });
      },
    },
    {
      id: MenuActionId.PATIENT_MERGE,
      label: this.translocoService.translate('Fusionner'),
      testId: 'action-patient-merge',
      icon: '@tui.users',
      condition: (patient: Patient): boolean => this.isPatientActive(patient),
      execute: (patient: Patient): void => {
        this.params$.pipe(first()).subscribe(({ clientCode, departmentId }) => {
          this.dialogService
            .openComponent<PatientMergeComponent, Patient>(PatientMergeComponent, {
              label: this.translocoService.translate('Fusionner deux patients'),
              data: {
                patient,
                clientCode,
                departmentId,
              },
            })
            .subscribe((targetPatient) => {
              if (targetPatient) {
                this.patientMergeService.mergePatient(patient, targetPatient).subscribe((merged) => {
                  if (merged) {
                    const url = PatientLinkDirective.getRoute(clientCode, targetPatient.id, departmentId);
                    this.router.navigate(url, { queryParamsHandling: 'merge' });
                  }
                });
              }
            });
        });
      },
    },
    {
      id: MenuActionId.PATIENT_TELEEXPERTISE_WITH_OMNIDOC,
      label: this.translocoService.translate('Téléexpertiser avec Omnidoc'),
      testId: 'action-patient-teleexpertise-omnidoc',
      icon: 'omnidoc',
      condition: (patient: Patient): boolean => this.isPatientActive(patient),
      execute: (patient: Patient): void => {
        this.params$.pipe(first()).subscribe(({ clientCode, departmentId }) => {
          this.dialogService
            .openComponentWithCloseConfirmation<PatientTeleexpertiseRequestComponent, any>(
              PatientTeleexpertiseRequestComponent,
              {
                label: this.translocoService.translate('Créer une téléexpertise avec Omnidoc'),
                size: 'l',
                appearance: 'fullscreen',
                data: {
                  patient,
                  clientCode,
                  departmentId,
                  teleexpertiseProviderType: TeleexpertiseProvider.OMNIDOC,
                },
              },
            )
            .subscribe();
        });
      },
    },
    {
      id: MenuActionId.PATIENT_TELEEXPERTISE_WITH_ROFIM,
      label: this.translocoService.translate('Téléexpertiser avec Rofim'),
      testId: 'action-patient-teleexpertise-rofim',
      icon: 'rofim',
      condition: (patient: Patient): boolean => this.isPatientActive(patient),
      execute: (patient: Patient): void => {
        this.params$.pipe(first()).subscribe(({ clientCode, departmentId }) => {
          this.dialogService
            .openComponentWithCloseConfirmation<PatientTeleexpertiseRequestComponent, any>(
              PatientTeleexpertiseRequestComponent,
              {
                label: this.translocoService.translate('Créer une téléexpertise avec Rofim'),
                size: 'l',
                appearance: 'fullscreen',
                data: {
                  patient,
                  clientCode,
                  departmentId,
                  teleexpertiseProviderType: TeleexpertiseProvider.ROFIM,
                },
              },
            )
            .subscribe();
        });
      },
    },
  ];

  isPatientActive(patient: Patient): boolean {
    return patient && !patient.isArchived;
  }

  isPatientArchived(patient: Patient): boolean {
    return patient && patient.isArchived;
  }
}
